// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <https://benrichardson.dev>
// Based on QuakeWatch by Ben Richardson — https://benrichardson.dev
import { svgHistogram, svgLine, type HistBin } from '../components/charts';
import { gterm, infoI } from '../components/overlays';
import { joules, mag as fmtMag, num } from '../format';
import { magColor, timeBuckets } from '../model';
import { quakes, state } from '../state';
import type { Period } from '../types';
import { emptyState } from './shared';

const WINDOW_MS: Record<Period, number> = {
  hour: 3600e3,
  day: 86400e3,
  week: 7 * 86400e3,
  month: 30 * 86400e3,
};
const N_BUCKETS: Record<Period, number> = { hour: 30, day: 24, week: 28, month: 30 };

export function renderTimeline(root: HTMLElement): void {
  const list = quakes();
  if (list.length === 0) { root.innerHTML = emptyState(); return; }

  const period = state.period;
  const end = state.feed?.generated ?? Math.max(...list.map((q) => q.time));
  const start = end - WINDOW_MS[period];
  const n = N_BUCKETS[period];
  const buckets = timeBuckets(list, n, start, end);

  const histBins: HistBin[] = buckets.map((b) => ({
    label: bucketLabel(b.start, period),
    count: b.count,
    color: b.count > 0 ? magColor(b.maxMag) : '#1e293b',
    tip: `${timeLabel(b.start, period)} – ${timeLabel(b.end, period)}\n${b.count} event${b.count === 1 ? '' : 's'}${b.count ? ` · largest M${fmtMag(b.maxMag)}` : ''}`,
  }));

  // cumulative energy
  let running = 0;
  const cumEnergy = buckets.map((b) => { running += b.energy; return running; });
  const totalE = running;

  const peak = buckets.reduce((a, b) => (b.count > a.count ? b : a), buckets[0]);
  const peakLabel = `${timeLabel(peak.start, period)} – ${timeLabel(peak.end, period)}`;

  root.innerHTML = `
    <div class="panel">
      <div class="panel-title">Events over the past ${period === 'hour' ? 'hour' : period === 'day' ? 'day' : period === 'week' ? '7 days' : '30 days'}</div>
      <div class="panel-sub">Number of earthquakes per time slot, coloured by the largest in each. Tight clusters often mark an ${gterm('aftershock', 'aftershock sequence')} or an ${gterm('swarm', 'earthquake swarm')}.</div>
      <div class="chart-wrap">${svgHistogram(histBins, { height: 240 })}</div>
      <div class="legend"><span>Bar height = event count · colour = largest magnitude in the slot</span></div>
    </div>

    <div class="panel" style="margin-top:1rem">
      <div class="panel-title">Cumulative energy released ${infoI('energy')}</div>
      <div class="panel-sub">Total ${gterm('energy', 'seismic energy')} building up across the window. Notice how it jumps in steps — each big step is a single large earthquake, not the steady drip of small ones.</div>
      <div class="chart-wrap">${svgLine(cumEnergy, { width: 900, height: 180, area: true, color: '#f97316' })}</div>
      <div class="legend"><span>Ends at ${joules(totalE)} total</span></div>
    </div>

    <div class="callout mt-lg">
      <strong>Busiest slot:</strong> ${num(peak.count)} earthquakes between ${peakLabel}${peak.count && Number.isFinite(peak.maxMag) ? `, up to M${fmtMag(peak.maxMag)}` : ''}.
      A sudden burst like this usually means a mainshock has set off a run of ${gterm('aftershock', 'aftershocks')} nearby.
    </div>`;
}

function bucketLabel(ms: number, period: Period): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  if (period === 'hour') return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
  if (period === 'day') return `${pad(d.getUTCHours())}h`;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d.getUTCDate()} ${months[d.getUTCMonth()]}`;
}

function timeLabel(ms: number, period: Period): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  if (period === 'hour' || period === 'day') return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
  return `${d.getUTCDate()} ${months[d.getUTCMonth()]}`;
}
